import { useEffect, useState } from "react";

import { Dialog } from "primereact/dialog";

import { TemplatePageType } from "models";
import { useAppSelector } from "utils/store";
import { renderCoverPdf, defaultBrowserImageResolver } from "utils/pdf";

type Props = {
  visible: boolean;
  onHide: () => void;
};

/**
 * Dev-only: renders the current cover page to an actual PDF (client-side pdfmake,
 * via @defectwise/pdf-report-core — the same converter + pdfmake version the
 * server uses) and shows it in a modal, with no server round-trip. Useful for
 * sanity-checking the shared-package cutover and any new node types (e.g. tables)
 * without needing a live preview session.
 */
export const CoverPdfPreview = ({ visible, onHide }: Props) => {
  const coverContent = useAppSelector(
    (state) => state.templateBuilderSlice.pages.find((p) => p.type === TemplatePageType.coverPage)?.content
  );
  const imageManifest = useAppSelector((state) => state.templateBuilderSlice.imageManifest);
  const storageToken = useAppSelector((state) => state.templateBuilderSlice.storageToken);

  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;

    let active = true;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    setUrl(null);

    renderCoverPdf((coverContent ?? []) as any, {
      resolveImage: defaultBrowserImageResolver,
      imageManifest,
      storageToken,
    })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((ex) => {
        // Surface the real error instead of failing silently.
        // eslint-disable-next-line no-console
        console.error("Cover PDF render failed", ex);
        if (active) setError(String(ex?.stack ?? ex?.message ?? ex));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [visible, coverContent, imageManifest, storageToken]);

  return (
    <Dialog
      header="Cover Preview (PDF) — dev only"
      visible={visible}
      onHide={onHide}
      style={{ width: "min(900px, 95vw)", height: "92vh" }}
      maximizable
      dismissableMask
    >
      {loading && <div className="p-4">Rendering…</div>}
      {error && (
        <pre className="p-3" style={{ color: "var(--red-600)", whiteSpace: "pre-wrap", fontSize: "0.8rem" }}>
          Failed to render cover PDF:{"\n"}
          {error}
        </pre>
      )}
      {url && <iframe title="Cover PDF" src={url} style={{ width: "100%", height: "78vh", border: 0 }} />}
    </Dialog>
  );
};
